import type { AudioFrameV2 } from '../frameProvider/frameProvider';
import type { AudioHandler } from './audioHandler';
import type { AudioWorkletRequest, AudioWorkletResponse } from './audioWorkletProtocol';
import type { QueuedAudioFrame } from './cutterCorePlayer';

export type WorkletAudioHandlerPostRequest = (request: AudioWorkletRequest) => void;

export interface Handlers {
  /**
   * Invoked when the audio samples actually start to be hearable
   * out of the speakers/headset.
   */
  onAudioFrameStartPlaying: () => void,

  /**
   * Invoked when the audio frame has been buffered for playout by
   * the audio worklet, which signals that we can buffer the next frame
   * if needed.
   */
  onAudioBufferDequeued: () => void,

  onCancelledPlayout: () => void,
}

export class WorkletAudioHandler implements AudioHandler {
  private bufferIdCounter : number = 0;
  private handlers : Record<string, Handlers> = { };
  private pendingRequests : AudioWorkletRequest[] = [];
  private postRequest: WorkletAudioHandlerPostRequest | null = null;
  private closeCallbacks : (() => void)[] = [];
  private sampleRate: number = 48000;
  private outputLatency: number | undefined;

  constructor() {
  }

  registerOnCloseCallback(onClose: () => void) {
    this.closeCallbacks.push(onClose);
  }

  handleResponse(response: AudioWorkletResponse): void {
    const handler = this.handlers[response.bufferId];
    if (handler !== undefined) {
      switch (response.type) {
        case 'buffer_dequeued': {
          handler.onAudioBufferDequeued();
          break;
        }

        case 'start_playing_buffer': {
          delete this.handlers[response.bufferId];
          handler.onAudioFrameStartPlaying();
          break;
        }

        case 'cancel_buffer': {
          delete this.handlers[response.bufferId];
          handler.onCancelledPlayout();
          break;
        }
      }
    } else {
      throw new Error('No such buffer id: ' + response.bufferId);
    }
  }

  initialize(
    sampleRate: number,
    outputLatency: number | undefined,
    postRequest: WorkletAudioHandlerPostRequest,
  ) {
    this.sampleRate = sampleRate;
    this.outputLatency = outputLatency;
    this.postRequest = postRequest;
    for (const request of this.pendingRequests.splice(0)) {
      this.sendRequest(request);
    }
  }

  close(): void {
    this.closeCallbacks.forEach((c) => c());
  }

  queueAudioFrame(audioFrame: QueuedAudioFrame): void {
    let samples = getFloatSamplesFromAudioFrame(audioFrame.audioFrame);
    const bufferId = this.bufferIdCounter.toString();

    if (audioFrame.audioFrame.channelCount !== 2) {
      throw new Error(`Only stereo is supported. Got audio frame with ${audioFrame.audioFrame.channelCount} channels.`);
    }

    this.bufferIdCounter += 1;
    this.handlers[bufferId] = {
      onAudioFrameStartPlaying: audioFrame.onAudioFrameStartPlaying,
      onAudioBufferDequeued: audioFrame.onAudioBufferDequeued,
      onCancelledPlayout: audioFrame.onCancelledPlayout,
    };

    audioFrame.audioFrame.buffer.close();

    const latencyInSeconds = this.outputLatency ?? 0;
    const latencyInSamples = Math.round(latencyInSeconds * this.sampleRate);
    this.sendRequest({ type: 'frame', bufferId, samples, latencyInSamples });
  }

  startAudioPlayback(): void {
    this.sendRequest({ type: 'start_playback' });
  }

  clearAudioQueue(): void {
    this.sendRequest({ type: 'clear_queue' });
  }

  stopAudioPlayback(finish: boolean): void {
    this.sendRequest({ type: 'stop_playback', finish });
  }

  sendRequest(req: AudioWorkletRequest) {
    if (this.postRequest !== null) {
      this.postRequest(req);
    } else {
      this.pendingRequests.push(req);
    }
  }
}

function getFloatSamplesFromAudioFrame(frame: AudioFrameV2): number[] {
  switch (frame.format) {
    case 'pcm16': {
      const inSamples = frame.buffer.asReadonlyInt16Array();
      const outSamples : number[] = Array.from({ length: inSamples.length });
      // eslint-disable-next-line no-bitwise
      const factor = 1.0 / (1 << 15);
      for (let i = 0; i < inSamples.length; i++) {
        outSamples[i] = inSamples[i] * factor;
      }
      return outSamples;
    }
    case 'float32': {
      const inSamples = frame.buffer.asReadonlyFloat32Array();
      const outSamples : number[] = Array.from({ length: inSamples.length });
      for (let i = 0; i < inSamples.length; i++) {
        outSamples[i] = inSamples[i];
      }
      return outSamples;
    }
  }
}


